import { supabase } from '../lib/supabase'

/**
 * One-click Descript transcript generation, requested from a Video or Audio
 * deliverable.
 *
 * No Descript credential can safely live in the browser, so a request does
 * not call Descript directly — it writes a pending row to `transcript_jobs`
 * and an unattended worker (a scheduled Claude task, six times a day) picks
 * it up, runs the deliverable's Drive link through Descript, and stores the
 * result via the same `insert_transcript_revision` path a manual upload uses.
 * Nothing here ever writes to `transcript_versions`: only to the queue, and
 * only ever reads the queue back for status.
 */

export type TranscriptJobStatus = 'pending' | 'processing' | 'done' | 'error'

export interface TranscriptJob {
  id: string
  status: TranscriptJobStatus
  requestedAt: number
  completedAt?: number
  errorMessage?: string
  descriptProjectUrl?: string
  resultDeliverableId?: string
  resultWordCount?: number
}

export interface RequestResult {
  ok: boolean
  error?: string
}

/* ---------------------------------------------------------------------------
 * Staleness
 *
 * The worker recovers from its own failures. What nothing covered was the
 * worker not running AT ALL — rows simply sat at `pending`, and a queue that
 * stopped three days ago looked exactly like one that started a minute ago.
 *
 * There is no heartbeat for this worker, so staleness is derived from the age
 * of the request. That is deliberate: a rule that depends on something running
 * is worthless for detecting that nothing is running.
 *
 * Only `pending` counts. `processing` is the worker's own territory and it
 * self-heals; treating it as stalled here would report one failure twice.
 * ------------------------------------------------------------------------- */

/**
 * How long a request may sit unclaimed before the queue is treated as stopped.
 *
 * Ten hours, raised from four on 23 Sep 2026 when the worker went from hourly
 * to six times a day (07:12–22:12 Dubai, every three hours). A request can
 * arrive seconds after a run, so one missed run is already six hours and the
 * overnight break is nine. Ten absorbs a genuinely missed run without crying
 * wolf and still names a dead queue the same working day.
 *
 * **Changing the worker's cron means changing this number too.** Out of step,
 * the dashboard either reports a stall every morning or never reports one.
 */
export const TRANSCRIPT_STALE_AFTER_MS = 10 * 60 * 60 * 1000

/** True when a request has waited long enough that nobody should still call it "generating". */
export function isTranscriptJobStalled(
  job: TranscriptJob | null,
  now: number = Date.now(),
): boolean {
  if (!job) return false
  return job.status === 'pending' && now - job.requestedAt > TRANSCRIPT_STALE_AFTER_MS
}

/** The short status line shown while a Descript job is in flight or has landed. */
export function transcriptJobLabel(
  job: TranscriptJob | null,
  now: number = Date.now(),
): string {
  if (!job) return ''
  if (isTranscriptJobStalled(job, now)) return 'Transcript request stalled'
  switch (job.status) {
    case 'pending':
      return 'Transcript requested'
    case 'processing':
      return 'Generating transcript…'
    case 'done':
      return 'Transcript ready'
    case 'error':
      return 'Transcript generation failed'
  }
}

function describe(error: unknown): string {
  const detail = error as { message?: string; code?: string } | null
  if (!detail) return ''
  return `${detail.message ?? String(error)}${detail.code ? ` (${detail.code})` : ''}`
}

function jobOf(row: Record<string, unknown>): TranscriptJob {
  const completed = row.completed_at ? new Date(String(row.completed_at)).getTime() : undefined
  return {
    id: String(row.id ?? ''),
    status: (row.status as TranscriptJobStatus) ?? 'pending',
    requestedAt: new Date(String(row.requested_at ?? '')).getTime() || Date.now(),
    completedAt: Number.isFinite(completed) ? completed : undefined,
    errorMessage: (row.error_message as string | null) ?? undefined,
    descriptProjectUrl: (row.descript_project_url as string | null) ?? undefined,
    resultDeliverableId: (row.result_deliverable_id as string | null) ?? undefined,
    resultWordCount: (row.result_word_count as number | null) ?? undefined,
  }
}

/** Postgres unique-violation — the one-live-job-per-deliverable index. */
const ALREADY_QUEUED = '23505'

/**
 * Queues a Descript transcript run for one Video or Audio deliverable.
 *
 * Resolves the deliverable's real database id from its project id and
 * deliverable_key first — the app only ever holds the key — so the queued job
 * always points at a real, existing deliverable.
 */
export async function requestTranscriptJob(input: {
  projectId: string
  deliverableKey: string
  sourceName: string
  sourceKind: 'video' | 'audio'
  sourceUrl: string
  requestedBy: string
}): Promise<RequestResult> {
  if (!supabase) return { ok: false, error: 'No database connection.' }
  if (!input.sourceUrl.trim()) {
    return { ok: false, error: 'Add a Drive link before generating a transcript.' }
  }

  const { data: deliverable, error: lookupError } = await supabase
    .from('deliverables')
    .select('id')
    .eq('project_id', input.projectId)
    .eq('deliverable_key', input.deliverableKey)
    .maybeSingle()

  if (lookupError) return { ok: false, error: describe(lookupError) }
  if (!deliverable?.id) return { ok: false, error: 'Deliverable not found in this project.' }

  const { error: insertError } = await supabase.from('transcript_jobs').insert({
    project_id: input.projectId,
    source_deliverable_id: deliverable.id,
    source_deliverable_key: input.deliverableKey,
    source_name: input.sourceName,
    source_kind: input.sourceKind,
    source_url: input.sourceUrl,
    requested_by: input.requestedBy,
  })

  /* A double-click used to queue the same video twice, 180ms apart, and the
     dashboard then raised the same alarm twice (26 Sep 2026). The database
     refuses the second one now; this turns that refusal into a sentence. */
  if (insertError) {
    const code = (insertError as { code?: string }).code
    if (code === ALREADY_QUEUED) {
      return { ok: false, error: 'A transcript for this is already in the queue.' }
    }
    return { ok: false, error: describe(insertError) }
  }
  return { ok: true }
}

/**
 * The most recent transcript job for a deliverable, by its stored key — so
 * the admin row can show "Generating…" or a failure without keeping its own
 * copy of the deliverable's real database id.
 */
export async function fetchLatestTranscriptJob(
  projectId: string,
  deliverableKey: string,
): Promise<TranscriptJob | null> {
  if (!supabase || !projectId || !deliverableKey) return null

  const { data, error } = await supabase
    .from('transcript_jobs')
    .select(
      'id, status, requested_at, completed_at, error_message, descript_project_url, result_deliverable_id, result_word_count',
    )
    .eq('project_id', projectId)
    .eq('source_deliverable_key', deliverableKey)
    .order('requested_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    console.warn(`[supabase] transcript job lookup failed: ${describe(error)}`)
    return null
  }
  if (!data) return null
  return jobOf(data as Record<string, unknown>)
}

export interface StalledTranscript {
  id: string
  projectId: string
  /** The app-side deliverable key, so the dashboard row can open the right one. */
  deliverableKey: string
  sourceName: string
  requestedBy?: string
  requestedAt: number
  /** Times the worker has picked this row up. Stamped before it calls Descript. */
  attempts: number
  lastAttemptAt?: number
  errorMessage?: string
}

/**
 * WHY A STALLED REQUEST IS STALLED — and the reason this is three sentences
 * rather than one.
 *
 * This line used to read "Descript has not picked it up — check the scheduled
 * worker is running", on every stalled row, whatever had actually happened. It
 * was a guess printed as a finding, and on 26 Sep it sent Tom looking at a
 * worker whose schedule was fine.
 *
 * `attempts` is stamped when the worker PICKS UP a row, before it calls
 * Descript — the cheap half of a claim, costing no retry budget (see the
 * 13 Sep claim-order fix, which is why the row is otherwise untouched). So:
 *
 *   never picked up   -> no run has reached the queue. The worker IS the suspect.
 *   picked up, silent -> runs start and die mid-job. The run is the suspect.
 *   picked up, error  -> it tried and failed. The message says why; print it.
 *
 * Each one sends a different person to a different place. Say which.
 */
export function stalledReason(job: StalledTranscript): string {
  if (job.errorMessage) return `The worker tried and failed — ${job.errorMessage}`
  if (job.attempts > 0) {
    const tries = job.attempts === 1 ? 'once' : `${job.attempts} times`
    return `The worker picked this up ${tries} but never finished — check the last run's log`
  }
  return 'No run has reached the queue — check the scheduled worker is running'
}

/**
 * Every request across the studio that has waited past the threshold.
 *
 * Read for the dashboard rather than for one deliverable: the failure this
 * detects is the whole queue stopping, and the person who notices it is not
 * necessarily the person who asked. Hits `transcript_jobs_pending_idx`, and is
 * capped — past a screenful the count is no longer the useful part.
 */
export async function fetchStalledTranscripts(
  now: number = Date.now(),
): Promise<StalledTranscript[]> {
  if (!supabase) return []

  const cutoff = new Date(now - TRANSCRIPT_STALE_AFTER_MS).toISOString()

  const { data, error } = await supabase
    .from('transcript_jobs')
    .select(
      'id, project_id, source_deliverable_key, source_name, requested_by, requested_at, attempts, last_attempt_at, error_message',
    )
    .eq('status', 'pending')
    .lt('requested_at', cutoff)
    .order('requested_at', { ascending: true })
    .limit(20)

  if (error) {
    console.warn(`[supabase] stalled transcript lookup failed: ${describe(error)}`)
    return []
  }

  return (data ?? []).map((row) => {
    const attempted = row.last_attempt_at
      ? new Date(String(row.last_attempt_at)).getTime()
      : undefined
    return {
      id: String(row.id ?? ''),
      projectId: String(row.project_id ?? ''),
      deliverableKey: String(row.source_deliverable_key ?? ''),
      sourceName: String(row.source_name ?? 'Transcript'),
      requestedBy: (row.requested_by as string | null) ?? undefined,
      requestedAt: new Date(String(row.requested_at ?? '')).getTime() || now,
      attempts: Number(row.attempts ?? 0) || 0,
      lastAttemptAt: Number.isFinite(attempted) ? attempted : undefined,
      errorMessage: (row.error_message as string | null) ?? undefined,
    }
  })
}
